import { useMemo, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Pager } from "@/components/design/Pager";
import { isAnalyzableDocument } from "@/lib/analyzable-documents";
import type { ExtractionDoc } from "./ExtractionDialog";

const DOCS_PER_PAGE = 24;

export function ExtractionDocumentPicker({ docs, groups = [], docsLoading = false, docsLoadError = false, selectedIds, room,
  onToggle, onToggleMany, disabled = false, label, idPrefix = "analysis", selectionIdPrefix = "analysis-docselect",
}: {
  docs: readonly ExtractionDoc[]; groups?: readonly { id: string; name: string }[]; docsLoading?: boolean; docsLoadError?: boolean;
  selectedIds: ReadonlySet<string>; room: number; onToggle: (id: string) => void;
  onToggleMany: (ids: string[], select: boolean) => void; disabled?: boolean; label?: ReactNode;
  idPrefix?: string; selectionIdPrefix?: string;
}) {
  const intl = useIntl();
  const [docQuery, setDocQuery] = useState("");
  const [docPage, setDocPage] = useState(0);
  const [folderPages, setFolderPages] = useState<Record<string, number>>({});
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Group the "Aus Documents" list by folder (mirrors the Documents view) so
  // it's clear which documents live where; loose ones trail under "Ohne Ordner".
  // A filter drops the folders and searches flat: among hundreds of documents,
  // paging to the right folder is not finding.
  const loadingLine = (
    <div className="text-smycles-meta text-ink-muted">
      <FormattedMessage
        id="save.extraction.loadingDocuments"
        defaultMessage="Loading documents …"
      />
    </div>
  );
  const searchLabel = intl.formatMessage({
    id: "save.extraction.searchDocuments",
    defaultMessage: "Search documents",
  });
  // Only what can actually be analysed - a picker whose sole purpose is picking
  // something to analyse has no business listing an archive. Newest first,
  // mirroring the Documents page: whoever just uploaded a file opens the
  // analysis to use it, and must not have to page to it.
  const ordered = useMemo(
    () =>
      docs
        .filter((d) => isAnalyzableDocument(d.fileName ?? d.name))
        .sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? ""))),
    [docs],
  );
  const filter = docQuery.trim().toLowerCase();
  const grouped = filter
    ? []
    : groups
        .map((g) => {
          const inFolder = ordered.filter((d) => d.groupId === g.id);
          const totalPages = Math.max(1, Math.ceil(inFolder.length / DOCS_PER_PAGE));
          const page = Math.min(folderPages[g.id] ?? 0, totalPages - 1);
          return {
            id: g.id,
            name: g.name,
            docs: inFolder,
            totalPages,
            page,
            shown: inFolder.slice(page * DOCS_PER_PAGE, page * DOCS_PER_PAGE + DOCS_PER_PAGE),
          };
        })
        .filter((g) => g.docs.length > 0);
  // A document whose folder is unknown (the folder query resolves separately and
  // may fail) trails with the loose ones rather than disappearing.
  const knownGroups = new Set(groups.map((g) => g.id));
  const visibleDocs = filter
    ? ordered.filter((d) => d.name.toLowerCase().includes(filter))
    : ordered.filter((d) => !d.groupId || !knownGroups.has(d.groupId));
  const docTotalPages = Math.max(1, Math.ceil(visibleDocs.length / DOCS_PER_PAGE));
  // Clamped, so a document deleted or uploaded under the reader can't strand
  // them on a page that no longer exists.
  const docPageClamped = Math.min(docPage, docTotalPages - 1);
  const pagedDocs = visibleDocs.slice(
    docPageClamped * DOCS_PER_PAGE,
    docPageClamped * DOCS_PER_PAGE + DOCS_PER_PAGE,
  );

  const docRow = (d: ExtractionDoc) => {
    return (
      <label
        key={d.id}
        id={`${idPrefix}-doc-${d.id}`}
        className="box-flat flex cursor-pointer items-center gap-2.5 bg-page px-3 py-2 text-smycles-meta hover:bg-ink/[0.06]"
      >
        <input
          id={`${selectionIdPrefix}-${d.id}`}
          type="checkbox"
          checked={selectedIds.has(d.id)}
          disabled={disabled || (!selectedIds.has(d.id) && room === 0)}
          onChange={() => onToggle(d.id)}
          className="h-4 w-4 shrink-0 accent-ink disabled:opacity-30"
        />
        <span id={`${idPrefix}-docname-${d.id}`} className="truncate">{d.name}</span>
      </label>
    );
  };

  return (
              <div>
                <div className="smycles-field-label mb-1.5 text-ink-muted">
                  {label ?? <FormattedMessage id="save.extraction.fromDocuments" defaultMessage="From Documents" />}
                </div>
                {docsLoadError ? (
                  <p role="alert" className="mb-2 text-smycles-meta text-ink">
                    <FormattedMessage
                      id="save.extraction.documentsLoadError"
                      defaultMessage="Saved documents could not be loaded. You can still upload a new file."
                    />
                  </p>
                ) : null}
                {ordered.length > 0 ? (
                  <div className="box-flat mb-2 flex items-center bg-page px-3 py-1.5">
                    <input
                      id={`${idPrefix}-search`}
                      type="search"
                      value={docQuery}
                      onChange={(e) => {
                        setDocQuery(e.target.value);
                        setDocPage(0);
                      }}
                      aria-label={searchLabel}
                      placeholder={searchLabel}
                      className="flex-1 bg-transparent text-smycles-meta placeholder:text-ink-muted focus:outline-none"
                    />
                  </div>
                ) : null}
                {ordered.length === 0 && !docsLoadError ? (
                  docsLoading ? (
                    loadingLine
                  ) : (
                    <div className="text-smycles-meta text-ink-muted">
                      {docs.length === 0 ? (
                        <FormattedMessage
                          id="save.extraction.noDocuments"
                          defaultMessage="No documents available."
                        />
                      ) : (
                        // Not "no documents" - the Documents page shows them.
                        <FormattedMessage
                          id="save.extraction.noAnalyzableDocuments"
                          defaultMessage="None of your documents can be analysed."
                        />
                      )}
                    </div>
                  )
                ) : (
                  <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
                    {grouped.map((g) => {
                      const ids = g.docs
                        .filter((d) => isAnalyzableDocument(d.fileName ?? d.name))
                        .map((d) => d.id);
                      const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
                      const open = !collapsed.has(g.id);
                      return (
                        <div key={g.id}>
                          <div className="mb-1 flex items-center gap-2 text-smycles-label tracking-wider text-ink-muted">
                            <button
                              type="button"
                              onClick={() => toggleCollapse(g.id)}
                              aria-label={intl.formatMessage({
                                id: "save.extraction.toggleCollapse",
                                defaultMessage: "Expand/collapse",
                              })}
                              className={`shrink-0 text-ink-muted transition-transform hover:text-ink ${open ? "rotate-90" : ""}`}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                className="h-3.5 w-3.5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                              >
                                <path d="m9 6 6 6-6 6" />
                              </svg>
                            </button>
                            <input
                              type="checkbox"
                              checked={allSelected}
                              disabled={disabled || ids.length === 0 || (!allSelected && room === 0)}
                              onChange={() => onToggleMany(ids, !allSelected)}
                              aria-label={intl.formatMessage(
                                {
                                  id: "save.extraction.selectAllInGroup",
                                  defaultMessage: "Select all in {group}",
                                },
                                { group: g.name },
                              )}
                              className="h-3.5 w-3.5 shrink-0 accent-ink"
                            />
                            <button
                              type="button"
                              onClick={() => toggleCollapse(g.id)}
                              className="flex min-w-0 items-center gap-2 tracking-wider"
                            >
                              <span id={`${idPrefix}-foldername-${g.id}`} className="truncate">{g.name}</span>
                              <span className="text-ink-muted">{g.docs.length}</span>
                            </button>
                          </div>
                          {open ? (
                            <>
                              <div className="space-y-1 pl-4">{g.shown.map(docRow)}</div>
                              <Pager
                                page={g.page}
                                totalPages={g.totalPages}
                                onChange={(next) =>
                                  setFolderPages((p) => ({ ...p, [g.id]: next }))
                                }
                              />
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                    {pagedDocs.length > 0 ? (
                      <div>
                        {grouped.length > 0 ? (
                          <div className="mb-1 text-smycles-label tracking-wider text-ink-muted">
                            <FormattedMessage
                              id="save.extraction.noFolder"
                              defaultMessage="No folder"
                            />
                          </div>
                        ) : null}
                        <div className="space-y-1">{pagedDocs.map(docRow)}</div>
                      </div>
                    ) : null}
                    {filter && visibleDocs.length === 0
                      ? docsLoading
                        ? loadingLine
                        : (
                            <div id={`${idPrefix}-no-matches`} className="text-smycles-meta text-ink-muted">
                              <FormattedMessage
                                id="save.extraction.noMatches"
                                defaultMessage="No matches"
                              />
                            </div>
                          )
                      : null}
                  </div>
                )}
                <Pager
                  page={docPageClamped}
                  totalPages={docTotalPages}
                  onChange={setDocPage}
                />
              </div>
  );
}
